import { ActivatedRoute, Params, Router } from '@angular/router';
import { PageEvent } from '@angular/material/paginator';
import { Paginator } from '../models/paginator';

type SerializableParamValue = string | string[] | number | number[] | undefined;

/**
 * Describes how a single filter field maps to/from URL query params.
 *
 * @typeParam TValue The TypeScript type of the filter field.
 *
 * @example
 * // Simple string filter (query text):
 * { paramKey: 'query', deserialize: v => typeof v === 'string' ? v : undefined, serialize: v => v || undefined }
 *
 * @example
 * // Multi-value number filter (class numbers):
 * { paramKey: 'classNumber', deserialize: v => Array.isArray(v) ? v.map(Number) : typeof v === 'string' ? [Number(v)] : [], serialize: v => v.length > 0 ? v : undefined }
 */
export interface QueryParamField<TValue = unknown> {
  // The query param key in the URL, e.g. 'classNumber', 'query', 'sort'.
  paramKey: string;

  // Parse the raw URL param value(s) into the typed filter value.
  // - `undefined` -> param was absent from the URL
  // - `string` -> single value (`?query=foo`)
  // - `string[]` -> repeated values (`?classNumber=1&classNumber=2`)
  // Should never throw - return a sensible default for unexpected shapes
  deserialize: (value: SerializableParamValue) => TValue;

  // Serialize the typed value back to URL param value(s).
  // Return `undefined` to omit the param entirely (cleaner URL when empty).
  // Return a `string[]` for repeated params (`?classNumber=1&classNumber=2`).
  serialize: (value: TValue) => SerializableParamValue;
}

/**
 * Configuration for {@link QueryParamManager}.
 *
 * @example
 * const config: QueryParamManagerConfig<TrademarkFilters> = {
 *   defaultLimit: 10,
 *   defaultSort: 'name',
 *   fields: {
 *     query: { paramKey: 'query', deserialize: optionalString, serialize: v => v || undefined },
 *     classNumber: { paramKey: 'classNumber', deserialize: numberArray, serialize: v => v.length ? v : undefined },
 *     countryOfDesignation: { paramKey: 'countryOfDesignation', deserialize: stringArray, serialize: v => v.length ? v : undefined },
 *     families: { paramKey: 'families', deserialize: stringArray, serialize: v => v?.length ? v : undefined },
 *   },
 * };
 *
 * @example
 * // Family filters with a 'family' namespace -> URL: ?familyPage=1&familyQuery=...
 * const familyConfig: QueryParamManagerConfig<TrademarkFamilyFilters> = {
 *   namespace: 'family',
 *   defaultLimit: 10,
 *   fields: {
 *     query: { paramKey: 'familyQuery', deserialize: optionalString, serialize: v => v || undefined },
 *   },
 * };
 */
export interface QueryParamManagerConfig<TFilters extends Record<string, unknown>> {
  // Optional namespace prefix for pagination params.
  // E.g. 'family' -> URL uses familyPage, familyLimit, familySort
  // instead of generic page, limit, sort.
  // This allows two managers on the same route (parent + child entities).
  namespace?: string;

  // Default items per page (default: 10)
  defaultLimit?: number;

  // optional default sort expression (like '-createdAt' or 'name')
  defaultSort?: string;

  /**
   * Map every filter key to its URL param definition.
   *
   * Every key in `TFilters` **must** have an entry here - the manager
   * uses this map to derive defaults and serialize/deserialize properly.
   */
  fields: {
    [K in keyof TFilters]: QueryParamField<TFilters[K]>;
  };
}

// region Generic serializers

export const stringArraySerializer = (v: string[]) => v.length > 0
  ? v
  : undefined;
export const numberArraySerializer = (v: number[]) => v.length > 0
  ? v
  : undefined;
export const optionalStringSerializer = (v?: string) => v || undefined;

// endregion

// region deserializers

export function optionalString(value: SerializableParamValue): string | undefined {
  if (typeof value === 'string') return value;
  return undefined;
}

export function stringArray(value: SerializableParamValue): string[] {
  if (Array.isArray(value)) return value.map(String);
  if (typeof value === 'string') return [value];
  return [];
}

export function numberArray(value: SerializableParamValue): number[] {
  if (Array.isArray(value)) return value.map(Number).filter(n => !Number.isNaN(n));
  if (typeof value === 'string') return [Number(value)];
  return [];
}

// endregion

/**
 * Generic manager for bidirectionnal URL query-param <-> state synchronization
 *
 * it handles:
 *
 * - Paginator state (page, limit, offset, sort) -- the manager owns a
 *   `Paginator<TEntity>` that can be read/written directly
 * - Filter state -- typed according to the `TFilters` interface provided.
 * - `fromParams(params)` -- parse URL query params into pager + filters.
 * - `toParams()` -- serialize the current state back to a `Params` object.
 * - `navigate(route)` -- push state to the URL (replace semantics).
 * - Common paginator event handlers: `handlePage()`, `onSortChange()`, `resetPage()`.
 */
export class QueryParamManager<TFilters extends Record<string, unknown>, TEntity = unknown> {
  private static readonly pagerMaxLimit = 100;

  pager: Paginator<TEntity> = {
    docs: [],
    page: 1,
    offset: 0,
    limit: 10,
    totalDocs: 0,
    sort: undefined,
  };

  filters = {} as TFilters;

  private readonly paramPrefix: string;

  constructor(
    private readonly config: QueryParamManagerConfig<TFilters>,
    private readonly router: Router,
  ) {
    // Namespace for pagination params: 'family' -> 'familyPage', 'familyLimit', etc.
    this.paramPrefix = config.namespace ? `${config.namespace}` : '';

    // Apply defaults
    if (config.defaultLimit !== undefined) {
      this.pager.limit = config.defaultLimit;
    }
    if (config.defaultSort !== undefined) {
      this.pager.sort = config.defaultSort;
    }

    // Initialize every filter field to its "absent" default
    const initial = {} as TFilters;
    for (const key of Object.keys(config.fields) as (keyof TFilters)[]) {
      const field = config.fields[key];
      initial[key] = field.deserialize(undefined);
    }
    this.filters = initial;
  }

  // region param key helpers (prefix when multiple pagination (tabs, for example))

  // defaults to 'page' if no namespace was provided in the config! 'namespacePage' otherwise
  private pageKey(): string {
    return this.paramPrefix.trim() ? `${this.paramPrefix}Page` : 'page';
  }

  // defaults to 'limit' if no namespace was provided in the config! 'namespaceLimit' otherwise
  private limitKey(): string {
    return this.paramPrefix.trim() ? `${this.paramPrefix}Limit` : 'limit';
  }

  // defaults to 'sort' if no namespace was provided in the config! 'namespaceSort' otherwise
  private sortKey(): string {
    return this.paramPrefix.trim() ? `${this.paramPrefix}Sort` : 'sort';
  }

  // endregion

  // region URL -> state

  // Read Params (from ActivatedRoute.queryParams) and apply them to the paginator and filter state
  fromParams(params: Params): void {
    // pagination
    const pageRaw = params[this.pageKey()] as string;
    const limitRaw = params[this.limitKey()] as string;
    const sortRaw = params[this.sortKey()] as string;

    if (pageRaw !== undefined) {
      const page = Number.parseInt(pageRaw, 10);
      this.pager.page = page > 0 ? page : 1;
      // hard cap limit at 100 elements
      const limit = limitRaw === undefined
        ? this.pager.limit
        : Number.parseInt(limitRaw, 10);
      this.pager.limit = Math.min(
        limit > 0 ? limit : this.pager.limit,
        QueryParamManager.pagerMaxLimit,
      );
      this.pager.offset = (this.pager.page - 1) * this.pager.limit;
    }
    if (sortRaw !== undefined) {
      this.pager.sort = sortRaw;
    }

    // filters
    for (const key of Object.keys(this.config.fields) as (keyof TFilters)[]) {
      const field = this.config.fields[key];
      this.filters[key] = field.deserialize(params[field.paramKey]);
    }
  }

  // endregion

  // region state -> URL

  /**
   * Serialize the current pager + filter state to a Params object suitable
   * for router.navigate([], { queryParams }).
   *
   * Only non-empty filter values are included. Pagination params are always
   * present so the URL remains shareable/bookmarkable.
   */
  toParams(): Params {
    const params: Params = {};

    // Always include pagination (makes URLs bookmarkable)
    params[this.pageKey()] = this.pager.page;
    params[this.limitKey()] = this.pager.limit;
    if (this.pager.sort) {
      params[this.sortKey()] = this.pager.sort;
    }

    // filters - only when non-empty
    for (const key of Object.keys(this.config.fields) as (keyof TFilters)[]) {
      const field = this.config.fields[key];
      const serialized = field.serialize(this.filters[key]);
      if (serialized !== undefined) {
        params[field.paramKey] = serialized;
      }
    }

    return params;
  }

  // endregion

  // Push the current state to the URL via `router.navigate`
  navigate(route: ActivatedRoute): Promise<boolean> {
    return this.router.navigate([], {
      relativeTo: route,
      queryParams: this.toParams(),
    });
  }

  // Paginator event handlers

  // Apply a Material PageEvent to the paginator.
  handlePage(event: PageEvent): void {
    this.pager.limit = event.pageSize;
    this.pager.page = event.pageIndex + 1;
    this.pager.offset = event.pageIndex * this.pager.limit;
  }

  // Apply a Material Sort event (MatSortChange) to the sort expression.
  onSortChange(event: { direction: string; active: string }): void {
    if (['asc', 'desc'].includes(event.direction)) {
      this.pager.sort = `${event.direction === 'desc' ? '-' : ''}${event.active}`;
    }
  }

  // Reset to page 1 (call when filters change)
  resetPage(): void {
    this.pager.page = 1;
    this.pager.offset = 0;
  }

  hasActiveFilters(): boolean {
    return (Object.keys(this.config.fields) as (keyof TFilters)[]).some((key) => {
      const field = this.config.fields[key];
      return field.serialize(this.filters[key]) !== undefined;
    });
  }

  resetFilters(): void {
    for (const key of Object.keys(this.config.fields) as (keyof TFilters)[]) {
      const field = this.config.fields[key];
      this.filters[key] = field.deserialize(undefined);
    }
  }
}
